import { useCallback, useEffect, useState } from 'react';
import { useUser } from './UserContext';
import { Modal } from '../Modal';
import { Input, Spinner } from '@cloudscape-design/components';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { createUser, updateUser } from '../../common/api/users';

export type UserSetupProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export function UserSetup(props: UserSetupProps) {
  const { user, setUser } = useUser();
  const [name, setName] = useState<string>(user?.name ?? '');

  // get full route from the current location
  const location = useLocation();

  const create = useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      setUser(data);
      props.setOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      setUser(data);
      props.setOpen(false);
    },
  });

  const loading = create.isPending || update.isPending;

  const triggerCreate = useCallback(() => {
    // set up user if not already set
    if (user) {
      return;
    }
    if (loading) {
      return;
    }
    // must be either /events/{eventUuid}, /scenarios/{scenarioUuid}, or /rooms/{roomUuid}
    // first, split the route by '/' and remove empty strings
    const routeParts = location.pathname.split('/').filter((part) => part);
    if (routeParts.length < 2) {
      return;
    }
    // next, save the first part of the route
    const entityType = routeParts[0];
    // next, save the second part of the route as the uuid
    const entityUuid = routeParts[1];
    if (entityType === 'events') {
      create.mutate({
        name: name.trim(),
        eventUuid: entityUuid,
      });
    } else if (entityType === 'scenarios') {
      create.mutate({
        name: name.trim(),
        scenarioUuid: entityUuid,
      });
    } else if (entityType === 'rooms') {
      create.mutate({
        name: name.trim(),
        roomUuid: entityUuid,
      });
    } else {
      console.error(`Invalid route to create user: ${location.pathname}`);
    }
  }, [create, location.pathname, name, user, setUser, loading]);

  const triggerUpdate = useCallback(() => {
    if (!user) {
      return;
    }
    if (loading) {
      return;
    }
    if (user.name === name.trim()) {
      props.setOpen(false);
      return;
    }
    update.mutate({
      userUuid: user.userUuid,
      name: name.trim(),
      userCode: user.userCode,
    });
  }, [update, user, name, loading]);

  useEffect(() => {
    if (user) {
      setName(user.name);
    }
  }, [user]);

  return (
    <Modal isOpen={props.open} onClose={() => props.setOpen(false)} stayOpen>
      <div className="px-4 py-4 space-y-2 text-white rounded-lg mx-auto my-auto grid grid-cols-1 gap-4">
        <div className="flex flex-col items-center text-center">
          <form className="space-y-4 items-center text-left">
            <div className="w-full flex flex-col items-start gap-2">
              <label className="block text-sm font-medium text-gray-200">Name</label>
              <Input
                name="name"
                disabled={loading}
                placeholder={user?.name}
                value={name}
                onChange={(e) => {
                  setName(e.detail.value);
                }}
              />
            </div>
            <div className="text-center">
              <button
                className={`text-black font-semibold text-sm py-2 px-2 rounded ${
                  loading || !name.trim() ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
                }`}
                disabled={loading || !name.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  user ? triggerUpdate() : triggerCreate();
                }}
                onKeyDown={(e) => {
                  e.preventDefault();
                  if (e.key === 'Enter') {
                    user ? triggerUpdate() : triggerCreate();
                  }
                }}
              >
                <div className="flex items-center justify-center w-full">
                  <span>
                    {user ? (user.name.trim() === name.trim() ? 'Continue' : 'Update') : 'Create'}
                  </span>
                  {loading ? (
                    <div className="ml-2 w-4">
                      <Spinner />
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </button>
              {/* {error && (
                <div className="mt-4">
                  <ErrorTurn error={error} />
                </div>
              )} */}
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
